import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import ProjectsActions from '@/Redux/Actions/ProjectsActions'

interface Props {
  selectedProject?: any
}

const ProjectSwitcher = ({ selectedProject }: Props) => {

  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const containerRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)

  const projectsData = useSelector((state: any) => state.ProjectsReducer.data)
  const loadResultStatus = useSelector((state: any) => state.ProjectsReducer.loadResultStatus)

  const projects = useMemo(() => {
    const availableProjects = [
      ...(projectsData?.mine || []),
      ...(projectsData?.other || []),
      ...(projectsData?.active || []),
    ]

    return availableProjects.reduce((result: any[], project: any) => {
      if (!project?._id || project.archived) return result
      if (result.some((item: any) => item._id === project._id)) return result

      result.push(project)
      return result
    }, [])
  }, [projectsData])

  useEffect(() => {
    const projectListIsLoadedByPage = location.pathname === '/' || location.pathname === '/other-projects'

    if (!projectListIsLoadedByPage && loadResultStatus === 'none') {
      dispatch(ProjectsActions.getResultAction())
    }
  }, [dispatch, loadResultStatus, location.pathname])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const handleSelectProject = (projectId?: string) => {
    setIsOpen(false)

    if (!projectId) {
      history.push('/')
      return
    }

    history.push(`/project/${projectId}`)
  }

  const selectedProjectName = selectedProject?.shortName || selectedProject?.name

  return (
    <div className="pl4-project-switcher-wrap" ref={containerRef}>
      <button
        type="button"
        className={`pl4-project-switcher ${selectedProject?._id ? '' : 'is-empty'}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className={`pl4-project-status ${selectedProject?._id ? '' : 'is-neutral'}`} />
        <span className="pl4-project-name">
          {selectedProjectName || 'Seleccionar proyecto'}
        </span>
        <span className="pl4-project-chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="pl4-project-dropdown" role="menu">
          <button
            type="button"
            className={!selectedProject?._id ? 'is-active' : ''}
            onClick={() => handleSelectProject()}
            role="menuitem"
          >
            <span className="pl4-project-dropdown__all icon-mis_proyectos" aria-hidden="true" />
            <span>Todos los proyectos</span>
          </button>
          {projects.length > 0 && <div className="pl4-project-dropdown__divider" />}
          <div className="pl4-project-dropdown__list">
            {projects.map((project: any) => (
              <button
                type="button"
                className={selectedProject?._id === project._id ? 'is-active' : ''}
                key={project._id}
                onClick={() => handleSelectProject(project._id)}
                role="menuitem"
              >
                <span className="pl4-project-dropdown__dot" aria-hidden="true" />
                <span>{project.shortName || project.name || 'Proyecto'}</span>
              </button>
            ))}
          </div>
          {loadResultStatus === 'loading' && (
            <span className="pl4-project-dropdown__status">Cargando proyectos...</span>
          )}
          {loadResultStatus !== 'loading' && projects.length === 0 && (
            <span className="pl4-project-dropdown__status">No hay proyectos disponibles</span>
          )}
        </div>
      )}
    </div>
  )
}

export default ProjectSwitcher
